import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Checkbox,
  FormInput,
  FormInputErrorMessage,
} from '@clearcover/atlas';
import { useFormikContext } from 'formik';
import { useAppContext } from 'QuoteApp/services/providers';
import { AddVehicleFormValues } from '.';
import { Label } from 'Shared/components/FormInput/Variants';
import styles from './styles.module.css';
import {
  getFilteredUsageDetailsOptions,
  UsageDetailsType,
} from 'QuoteApp/components/VehiclesList/VehicleForm/options';
import { Column, Row } from 'Shared/components';
import { stringToBoolean } from 'Shared/utils';

export type KeyOfDetails = keyof AddVehicleFormValues['usageDetails'];

export const VehicleUsage = () => {
  const { setFieldValue, values } = useFormikContext<AddVehicleFormValues>();
  const { config } = useAppContext();
  const [isNoneOfTheAboveDisabled, setIsNoneOfTheAboveDisabled] = useState(
    false
  );

  const questionKeys = useMemo(() => Object.keys(values.usageDetails), [
    values.usageDetails,
  ]);
  const filteredKeys = useMemo(() => {
    return Object.keys(values?.usageDetails)
      .filter(key => key !== 'noneOfTheAbove')
      .filter(key =>
        key === 'usedInMexico'
          ? config?.vehicles?.usageDetails?.usedInMexico?.required
          : true
      );
  }, [
    values?.usageDetails,
    config?.vehicles?.usageDetails?.usedInMexico?.required,
  ]) as KeyOfDetails[];

  const allValuesUnchecked = useMemo(() => {
    return filteredKeys.every(
      (key: KeyOfDetails) => !values?.usageDetails?.[key]
    );
  }, [filteredKeys, values?.usageDetails]);

  const [doesUsagesApply, setDoesUsagesApply] = useState<boolean | undefined>(
    values.identifier ? !allValuesUnchecked : undefined
  );

  const setAllUsagesToFalse = useCallback(() => {
    for (const key of filteredKeys) {
      setFieldValue(`usageDetails.${key}`, false);
    }
  }, [filteredKeys, setFieldValue]);

  useEffect(() => {
    if (isNoneOfTheAboveDisabled) {
      setFieldValue('usageDetails.noneOfTheAbove', false);
    }
    if (!doesUsagesApply) {
      setFieldValue('usageDetails.noneOfTheAbove', true);
    }
  }, [isNoneOfTheAboveDisabled, setFieldValue, doesUsagesApply]);

  useEffect(() => {
    setIsNoneOfTheAboveDisabled(!allValuesUnchecked);
  }, [allValuesUnchecked, setIsNoneOfTheAboveDisabled]);

  useEffect(() => {
    if (!allValuesUnchecked) {
      for (const key of filteredKeys) {
        const val = !!values?.usageDetails?.[key];
        if (values?.usageDetails?.[key] === val) continue;
        setFieldValue(`usageDetails.${key}`, val);
      }
    } else if (
      values?.usageDetails?.noneOfTheAbove &&
      doesUsagesApply !== false
    ) {
      setAllUsagesToFalse();
    }
  }, [
    setFieldValue,
    filteredKeys,
    values?.usageDetails,
    allValuesUnchecked,
    setAllUsagesToFalse,
    doesUsagesApply,
  ]);

  const vehicleUsageQuestions = getFilteredUsageDetailsOptions(
    config?.vehicles?.usageDetails as UsageDetailsType
  ).filter(question => {
    return questionKeys.includes(question.key);
  });

  const required = useMemo(() => {
    if (doesUsagesApply === undefined) return true;

    if (
      doesUsagesApply &&
      allValuesUnchecked &&
      !values?.usageDetails?.noneOfTheAbove
    )
      return true;

    return false;
  }, [
    allValuesUnchecked,
    doesUsagesApply,
    values?.usageDetails?.noneOfTheAbove,
  ]);

  const renderForm = () => (
    <>
      <Row className={styles.labelWrapper}>
        <Label className={styles.vehicleUsageLabel}>
          Please confirm which of these scenarios apply to this vehicle.
        </Label>
        {required && (
          <FormInputErrorMessage
            message="Required"
            className={styles.usageDetailsError}
          />
        )}
      </Row>
      <Row className={styles.vehicleUsageQuestionsRow}>
        {vehicleUsageQuestions.map(question => (
          <Checkbox
            key={question.key}
            name={question.key}
            label={question.label}
            value={!!values?.usageDetails?.[question.key as KeyOfDetails]}
            onChange={value => {
              setFieldValue(`usageDetails.${question.key}`, value);
            }}
            data-testid={`usage-${question.key}`}
          />
        ))}
        <Checkbox
          name="noneOfTheAbove"
          label="None of these apply."
          value={!!values?.usageDetails?.noneOfTheAbove}
          disabled={isNoneOfTheAboveDisabled}
          onChange={value => {
            setFieldValue('usageDetails.noneOfTheAbove', value);
          }}
          data-testid="usage-none-of-the-above"
        />
      </Row>
      <Row>
        <Alert
          type="info"
          message="Take a moment to double-check your answers before submitting."
        />
      </Row>
    </>
  );

  const renderReadOnlyQuestion = () => (
    <>
      <Row className={styles.labelWrapper}>
        <Label className={styles.vehicleUsageLabel}>
          Do any of these scenarios apply to this vehicle?
        </Label>
        {required && (
          <FormInputErrorMessage
            message="Required"
            className={styles.usageDetailsError}
          />
        )}
      </Row>
      <Row>
        <ul className={styles.vehicleUsageList}>
          {vehicleUsageQuestions.map(option => (
            <li key={option.key}>
              <Label>{option.label}</Label>
            </li>
          ))}
        </ul>
      </Row>
      <Row>
        <Column variant="third">
          <FormInput
            data-testid="does-vehicle-usage-apply"
            name={`does-vehicle-usage-apply`}
            value={doesUsagesApply?.toString() || ''}
            variant={'radioButtonGroup'}
            onChange={(value: string) => {
              const booleaValue = stringToBoolean(value);
              setDoesUsagesApply(booleaValue);
              setAllUsagesToFalse();

              setFieldValue('usageDetails.noneOfTheAbove', !booleaValue);
            }}
            options={{ true: 'Yes', false: 'No' }}
          />
        </Column>
      </Row>
    </>
  );

  return (
    <div className={styles.vehicleUsage}>
      {doesUsagesApply ? renderForm() : renderReadOnlyQuestion()}
    </div>
  );
};
